import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

interface Lease {
	id: string;
	leaseCode?: string; // Auto-generated lease code
	tenant: {
		name: string;
		email: string;
		avatar?: string;
	};
	property: {
		name: string;
		unit: string;
		address?: string;
	};
	startDate: string;
	endDate: string;
	rentAmount: number;
	securityDeposit: number;
	status: string;
	daysToExpiry: number;
	term: string;
	renewalDeadline?: string;
	terms?: string;
	paymentSchedule?: Array<{ date: string; amount: number; status: string }>;
	amendments?: Array<{ type: string; date: string }>;
	documents?: Array<{ name: string; url?: string }>;
}

interface LeaseTableProps {
	leases: Lease[];
	onEditLease: (leaseId: string) => void;
	onRenewLease: (leaseId: string) => void;
	onDeleteLease?: (leaseId: string) => void;
	onGenerateDocument: (leaseId: string) => void;
	onSendNotification: (leaseId: string) => void;
	onLeaseSelection?: (leaseId: string, isSelected: boolean) => void;
	selectedLeases?: string[];
	onLeaseClick?: (lease: Lease) => void;
}

const LeaseTable: React.FC<LeaseTableProps> = ({
	leases,
	onEditLease,
	onRenewLease,
	onDeleteLease,
	onGenerateDocument,
	onSendNotification,
	onLeaseSelection,
	selectedLeases = [],
	onLeaseClick,
}) => {
	const [sortConfig, setSortConfig] = useState<{
		key: string | null;
		direction: "asc" | "desc";
	}>({ key: null, direction: "asc" });


	const handleSort = (key: string) => {
		let direction: "asc" | "desc" = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}
		setSortConfig({ key, direction });
	};

	const handleSelectLease = (leaseId: string, checked: boolean) => {
		if (onLeaseSelection) {
			onLeaseSelection(leaseId, checked);
		}
	};

	const handleSelectAll = (checked: boolean) => {
		leases.forEach((lease) => {
			if (onLeaseSelection) {
				onLeaseSelection(lease.id, checked);
			}
		});
	};

	const isAllSelected =
		leases.length > 0 &&
		leases.every((lease) => selectedLeases.includes(lease.id));
	const isIndeterminate =
		selectedLeases.length > 0 && selectedLeases.length < leases.length;

	const getStatusBadge = (status: string, daysToExpiry: number) => {
		let badgeClass = "";
		let statusText = status;

		if (status === "active") {
			if (daysToExpiry <= 30) {
				badgeClass = "bg-error/10 text-error border-error/20";
				statusText = "Expiring Soon";
			} else if (daysToExpiry <= 60) {
				badgeClass = "bg-warning/10 text-warning border-warning/20";
				statusText = "Active";
			} else {
				badgeClass = "bg-success/10 text-success border-success/20";
				statusText = "Active";
			}
		} else if (status === "pending") {
			badgeClass = "bg-warning/10 text-warning border-warning/20";
			statusText = "Pending Renewal";
		} else if (status === "expired") {
			badgeClass = "bg-error/10 text-error border-error/20";
			statusText = "Expired";
		} else if (status === "terminated") {
			badgeClass = "bg-muted/10 text-muted-foreground border-border";
			statusText = "Terminated";
		}

		return (
			<span
				className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${badgeClass}`}
			>
				{statusText}
			</span>
		);
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getSortIcon = (key: string) => {
		if (sortConfig.key !== key) {
			return (
				<Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
			);
		}
		return sortConfig.direction === "asc" ? (
			<Icon name="ArrowUp" size={16} className="text-primary" />
		) : (
			<Icon name="ArrowDown" size={16} className="text-primary" />
		);
	};

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			{/* Table with horizontal scroll on mobile */}
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							{onLeaseSelection && (
								<th className="w-12 p-4">
									<input
										type="checkbox"
										checked={isAllSelected}
										ref={(input) => {
											if (input) input.indeterminate = isIndeterminate;
										}}
										onChange={(e) => handleSelectAll(e.target.checked)}
										className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
									/>
								</th>
							)}
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<div className="flex items-center space-x-2">
									<span>Lease Code</span>
								</div>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<div className="flex items-center space-x-2">
									<span>Tenant</span>
								</div>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("property")}
									className="flex items-center space-x-2 hover:text-primary smooth-transition"
								>
									<span>Property</span>
									{getSortIcon("property")}
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("startDate")}
									className="flex items-center space-x-2 hover:text-primary smooth-transition"
								>
									<span>Start Date</span>
									{getSortIcon("startDate")}
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("endDate")}
									className="flex items-center space-x-2 hover:text-primary smooth-transition"
								>
									<span>End Date</span>
									{getSortIcon("endDate")}
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("rentAmount")}
									className="flex items-center space-x-2 hover:text-primary smooth-transition"
								>
									<span>Rent Amount</span>
									{getSortIcon("rentAmount")}
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<span>Status</span>
							</th>
							<th className="text-right p-4 font-medium text-xs text-foreground">
								<span>Actions</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{leases.map((lease) => (
							<tr
								key={lease.id}
								className="border-b border-border hover:bg-muted/30 smooth-transition cursor-pointer"
								onClick={() => onLeaseClick && onLeaseClick(lease)}
							>
								{onLeaseSelection && (
									<td className="p-4" onClick={(e) => e.stopPropagation()}>
										<input
											type="checkbox"
											checked={selectedLeases.includes(lease.id)}
											onChange={(e) =>
												handleSelectLease(lease.id, e.target.checked)
											}
											className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
										/>
									</td>
								)}
								<td className="p-4">
									<span className="font-mono text-sm font-medium text-foreground">
										{lease.leaseCode || "N/A"}
									</span>
								</td>
								<td className="p-4">
									<div className="flex items-center space-x-3">
										<Image
											src={lease.tenant.avatar || ""}
											alt={lease.tenant.name}
											className="w-10 h-10 rounded-full object-cover"
										/>
										<div>
											<p className="font-medium text-foreground">
												{lease.tenant.name}
											</p>
											<p className="text-sm text-muted-foreground">
												{lease.tenant.email}
											</p>
										</div>
									</div>
								</td>
								<td className="p-4">
									<div>
										<p className="font-medium text-foreground">
											{lease.property.name}
										</p>
										<p className="text-sm text-muted-foreground">
											{lease.property.unit}
										</p>
									</div>
								</td>
								<td className="p-4 text-foreground">
									{formatDate(lease.startDate)}
								</td>
								<td className="p-4 text-foreground">
									{formatDate(lease.endDate)}
								</td>
								<td className="p-4 font-medium text-foreground">
									{formatCurrency(lease.rentAmount)}
								</td>
								<td className="p-4">
									{getStatusBadge(lease.status, lease.daysToExpiry)}
								</td>
								<td className="p-4" onClick={(e) => e.stopPropagation()}>
									<div className="flex items-center justify-end space-x-1">
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onEditLease(lease.id)}
											className="h-8 w-8"
										>
											<Icon name="Edit" size={16} />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onRenewLease(lease.id)}
											className="h-8 w-8 text-success hover:text-success"
										>
											<Icon name="RefreshCw" size={16} />
										</Button>
										{onDeleteLease && (
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onDeleteLease(lease.id)}
												className="h-8 w-8 text-destructive hover:text-destructive"
											>
												<Icon name="Trash2" size={16} />
											</Button>
										)}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				</div>
			</div>

			{/* Mobile Cards */}
			<div className="lg:hidden">
				{leases.map((lease) => (
					<div
						key={lease.id}
						className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/30 smooth-transition"
						onClick={() => onLeaseClick && onLeaseClick(lease)}
					>
						<div className="p-4">
							{onLeaseSelection && (
								<div className="mb-3" onClick={(e) => e.stopPropagation()}>
									<input
										type="checkbox"
										checked={selectedLeases.includes(lease.id)}
										onChange={(e) =>
											handleSelectLease(lease.id, e.target.checked)
										}
										className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
									/>
								</div>
							)}
							<div className="flex items-center justify-between mb-3">
								<div className="flex items-center space-x-3">
									<Image
										src={lease.tenant.avatar || ""}
										alt={lease.tenant.name}
										className="w-10 h-10 rounded-full object-cover"
									/>
									<div>
										<p className="font-medium text-foreground">
											{lease.tenant.name}
										</p>
										<p className="text-sm text-muted-foreground">
											{lease.property.name}
										</p>
									</div>
								</div>
								{getStatusBadge(lease.status, lease.daysToExpiry)}
							</div>

							<div className="grid grid-cols-2 gap-4 mb-4">
								<div>
									<p className="text-xs text-muted-foreground">Rent Amount</p>
									<p className="font-medium text-foreground">
										{formatCurrency(lease.rentAmount)}
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">End Date</p>
									<p className="font-medium text-foreground">
										{formatDate(lease.endDate)}
									</p>
								</div>
							</div>

							<div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => onEditLease(lease.id)}
									iconName="Edit"
								/>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => onRenewLease(lease.id)}
									iconName="RefreshCw"
								/>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default LeaseTable;
